import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import './product-assistant.css'

type ChatLink = { label: string; href: string }

type ChatTurn = {
  role: 'user' | 'assistant'
  content: string
  links?: ChatLink[]
}

const BOOTSTRAP_FALLBACK = {
  title: 'Perfect Predict Assistant',
  subtitle: 'Product questions in English',
  welcome:
    'Hi — I can help with Perfect Predict product questions in English: accounts, deposits, Create Prediction, IB, and risk notices.',
  chips: [
    'What is Perfect Predict?',
    'How do I deposit USDT?',
    'Demo vs Live?',
    'What is SAFE strategy?',
    'How do I create an account?',
  ],
}

async function postChat(message: string, history: ChatTurn[]) {
  const res = await fetch('/api/public/assistant/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      message,
      history: history.slice(-6).map((t) => ({ role: t.role, content: t.content.slice(0, 500) })),
    }),
  })
  const data = (await res.json().catch(() => null)) as
    | {
        answer?: string
        links?: ChatLink[]
        chips?: string[]
        error?: { message?: string }
      }
    | null
  if (!res.ok) {
    throw new Error(data?.error?.message || 'Assistant is temporarily unavailable.')
  }
  if (!data?.answer) {
    throw new Error('Assistant returned an empty reply.')
  }
  return data
}

/** Floating English-only product Q&A for the public website. */
export function ProductAssistant() {
  const panelId = useId()
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [chips, setChips] = useState<string[]>(BOOTSTRAP_FALLBACK.chips)
  const [meta, setMeta] = useState(BOOTSTRAP_FALLBACK)
  const [turns, setTurns] = useState<ChatTurn[]>([
    { role: 'assistant', content: BOOTSTRAP_FALLBACK.welcome },
  ])
  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let cancelled = false
    void fetch('/api/public/assistant/bootstrap', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data?.welcome) return
        setMeta({
          title: data.title || BOOTSTRAP_FALLBACK.title,
          subtitle: data.subtitle || BOOTSTRAP_FALLBACK.subtitle,
          welcome: data.welcome,
          chips: Array.isArray(data.chips) && data.chips.length ? data.chips : BOOTSTRAP_FALLBACK.chips,
        })
        setChips(
          Array.isArray(data.chips) && data.chips.length ? data.chips : BOOTSTRAP_FALLBACK.chips,
        )
        setTurns([{ role: 'assistant', content: data.welcome }])
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!open) return
    const el = listRef.current
    if (el) el.scrollTop = el.scrollHeight
    inputRef.current?.focus()
  }, [open, turns, busy])

  async function ask(question: string) {
    const q = question.trim()
    if (!q || busy) return
    const prior = turns
    const nextUser: ChatTurn = { role: 'user', content: q }
    setTurns([...prior, nextUser])
    setInput('')
    setBusy(true)
    try {
      const data = await postChat(q, prior)
      setTurns((cur) => [
        ...cur,
        {
          role: 'assistant',
          content: data.answer || '',
          links: data.links,
        },
      ])
      if (Array.isArray(data.chips) && data.chips.length) setChips(data.chips)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong.'
      setTurns((cur) => [
        ...cur,
        {
          role: 'assistant',
          content: `${message} You can also email support@perfectpredict.com.`,
          links: [{ label: 'Portal support', href: 'https://portal.perfectpredict.com/app/support' }],
        },
      ])
    } finally {
      setBusy(false)
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    void ask(input)
  }

  return (
    <div className="pp-assist" data-open={open ? '1' : '0'}>
      {open ? (
        <section
          className="pp-assist__panel"
          id={panelId}
          role="dialog"
          aria-label={meta.title}
          aria-modal="false"
        >
          <header className="pp-assist__head">
            <div>
              <p className="pp-assist__title">{meta.title}</p>
              <p className="pp-assist__sub">{meta.subtitle}</p>
            </div>
            <button
              type="button"
              className="pp-assist__icon-btn"
              aria-label="Close assistant"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </header>

          <div className="pp-assist__messages" ref={listRef}>
            {turns.map((turn, i) => (
              <div
                key={`${turn.role}-${i}`}
                className={`pp-assist__bubble pp-assist__bubble--${turn.role}`}
              >
                <p className="pp-assist__text">{turn.content}</p>
                {turn.links && turn.links.length > 0 ? (
                  <ul className="pp-assist__links">
                    {turn.links.map((link) => (
                      <li key={link.href}>
                        <a href={link.href} target="_blank" rel="noopener noreferrer">
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
            {busy ? <p className="pp-assist__typing">Thinking…</p> : null}
          </div>

          {!busy && turns.length < 4 ? (
            <div className="pp-assist__chips" aria-label="Suggested questions">
              {chips.map((chip) => (
                <button key={chip} type="button" onClick={() => void ask(chip)} disabled={busy}>
                  {chip}
                </button>
              ))}
            </div>
          ) : null}

          <form className="pp-assist__form" onSubmit={onSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              maxLength={500}
              disabled={busy}
              placeholder="Ask in English…"
              aria-label="Ask a product question in English"
              onChange={(e) => setInput(e.target.value)}
              autoComplete="off"
            />
            <button type="submit" disabled={busy || !input.trim()}>
              Send
            </button>
          </form>
          <p className="pp-assist__disclaimer">
            Product info only — not financial advice. Trading involves risk of loss.
          </p>
        </section>
      ) : null}

      <button
        type="button"
        className="pp-assist__launcher"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Close' : 'Ask'}
      </button>
    </div>
  )
}
